import {
  createElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from 'react';

import { cn } from '@/shared/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'outline'
  | 'outlinePrimary'
  | 'outlineDanger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** Lucide, Heroicons, or any SVG icon component. */
export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
export type ButtonIcon = ReactNode | IconComponent;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  /** Icon rendered before the label */
  leftIcon?: ButtonIcon;
  /** Icon rendered after the label */
  rightIcon?: ButtonIcon;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary shadow-sm',
  secondary:
    'bg-muted text-body hover:bg-border focus-visible:ring-2 focus-visible:ring-content-border',
  ghost:
    'text-secondary hover:bg-muted focus-visible:ring-2 focus-visible:ring-content-border',
  danger:
    'bg-danger text-white hover:bg-danger/90 focus-visible:ring-2 focus-visible:ring-danger',
  outline:
    'border bg-card text-body hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary',
  outlinePrimary:
    'border border-primary bg-transparent text-primary hover:bg-primary hover:text-white focus-visible:ring-2 focus-visible:ring-primary',
  outlineDanger:
    'border border-danger/15 bg-danger/10 text-danger hover:text-white hover:bg-danger focus-visible:ring-2 focus-visible:ring-danger',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-4 text-base gap-2',
};

const iconSizeStyles: Record<ButtonSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-4 w-4',
};

function isIconComponent(icon: ButtonIcon): icon is IconComponent {
  if (typeof icon === 'function') return true;

  return (
    typeof icon === 'object' &&
    icon !== null &&
    'render' in icon &&
    typeof (icon as { render: unknown }).render === 'function'
  );
}

function renderButtonIcon(icon: ButtonIcon, size: ButtonSize): ReactNode {
  if (isValidElement(icon)) {
    return icon;
  }

  if (isIconComponent(icon)) {
    return createElement(icon, {
      className: iconSizeStyles[size],
      'aria-hidden': true,
    });
  }

  return icon;
}

function LoadingSpinner({ size }: { size: ButtonSize }) {
  return (
    <svg
      className={cn(iconSizeStyles[size], 'animate-spin')}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

/**
 * Accessible button with loading state, optional icons, and multiple variants.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const leftIconNode = leftIcon ? renderButtonIcon(leftIcon, size) : null;
    const rightIconNode = rightIcon ? renderButtonIcon(rightIcon, size) : null;

    return (
      <button
        ref={ref}
        disabled={Boolean(disabled) || isLoading}
        aria-busy={isLoading}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-[8px] font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-content-bg',
          'disabled:pointer-events-none disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner size={size} />
        ) : (
          leftIconNode && (
            <span className="inline-flex shrink-0">{leftIconNode}</span>
          )
        )}
        {children}
        {!isLoading && rightIconNode && (
          <span className="inline-flex shrink-0">{rightIconNode}</span>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
